import { Manifest } from '@sketch-hq/sketch-prototype-player'
import { ErrorHandler } from '@sketch/tracing'
import { useEffect, useState } from 'react'
import { useInitialPrototypeArtboardId } from '../useInitialPrototypeArtboardId'
import { LoadManifestResult } from './types'

type UsePrototypeManifestFileProps = {
  downloadUrl?: string | null
}

/**
 * Loads a Prototype Manifest JSON file provided by the backend.
 */
export function usePrototypeManifest(
  props: UsePrototypeManifestFileProps
): LoadManifestResult {
  const { downloadUrl } = props
  const initialArtboardId = useInitialPrototypeArtboardId()
  const [result, setResult] = useState<LoadManifestResult>({
    status: 'loading',
    manifest: null,
  })

  useEffect(() => {
    async function loadManifest() {
      if (!downloadUrl) return // Skip while the downloadUrl is undefined

      try {
        const res = await fetch(downloadUrl)

        if (!res.ok) {
          ErrorHandler.shouldNeverHappen(
            'Prototype manifest.json network request failed'
          )
          setResult({ status: 'error', code: 'network_error', manifest: null })
          return
        }

        try {
          const manifest: unknown = await res.json()

          if (!validateManifest(manifest)) {
            ErrorHandler.shouldNeverHappen(
              'Prototype manifest.json file appears to be invalid'
            )
            setResult({
              status: 'error',
              code: 'invalid_manifest',
              manifest: null,
            })
            return
          }

          if (!isInitialArtboardInManifest(manifest, initialArtboardId)) {
            ErrorHandler.shouldNeverHappen(
              'Entrypoint Artboard not found in prototype manifest.json file'
            )
            setResult({
              status: 'error',
              code: 'artboard_not_found',
              manifest: null,
            })
            return
          }

          setResult({ status: 'success', manifest })
        } catch (parseError) {
          ErrorHandler.shouldNeverHappen(
            'Failed to parse prototype manifest.json file'
          )
          setResult({
            status: 'error',
            code: 'invalid_manifest',
            manifest: null,
          })
        }
      } catch (fetchError) {
        // No need to track this error, it's more likely due to
        // user's local network conditions
        setResult({ status: 'error', code: 'network_error', manifest: null })
      }
    }

    loadManifest()
  }, [downloadUrl, initialArtboardId])

  return result
}

/**
 * Do an extremely basic smoke test on the Manifest data format. We may revisit
 * this if we get a Manifest schema in future and a way to validate it more
 * rigorously.
 */
function validateManifest(manifest: unknown): manifest is Manifest {
  if (
    manifest !== null &&
    typeof manifest === 'object' &&
    'contents' in manifest
  ) {
    return true
  } else {
    return false
  }
}

/**
 * Check to see if the entrypoint Artboard in the url is available in the
 * Manifest.
 */
function isInitialArtboardInManifest(
  manifest: Manifest,
  initialArtboardId: string
): boolean {
  const artboards = manifest.contents.pages.flatMap(page => page.artboards)
  return !!artboards.find(artboard => artboard.id === initialArtboardId)
}
